import firebase from "firebase/app";
import "firebase/messaging";
import env from "config/env";
import { mobileOS } from "utils/lib";
import { postFCMToken } from "api/socket.request";
import { LOCAL_STORAGE_KEY } from "config/const";

const firebaseConfig = {
  apiKey: env.FIREBASE.API_KEY,
  authDomain: env.FIREBASE.AUTH_DOMAIN,
  projectId: env.FIREBASE.PROJECT_ID,
  storageBucket: env.FIREBASE.STORAGE_BUCKET,
  messagingSenderId: env.FIREBASE.MEASUREMENT_ID,
  appId: env.FIREBASE.APP_ID,
};

firebase.initializeApp(firebaseConfig);

let messaging: firebase.messaging.Messaging | undefined; // messaging이 초기화되지 않을 가능성을 고려하여 `undefined`를 허용

const os = mobileOS();
const isPWA = window.matchMedia("(display-mode: standalone)").matches;

document.onreadystatechange = () => {
  if ("serviceWorker" in navigator && document.readyState === "complete") {
    let intervalId: NodeJS.Timeout;
    navigator.serviceWorker
      .register("/firebase-messaging-sw.js")
      .then((registration) => {
        // 최초 1회 업데이트
        registration.update();

        // 서비스 워커가 활성화되었을 때
        if (registration.active) {
          console.log("서비스워커가 활성화 되었습니다.");
        }

        // 1시간마다 업데이트 체크
        intervalId = setInterval(() => {
          registration
            .update()
            .then(() => {
              console.log("Service Worker updated");
            })
            .catch((error) => {
              console.error("Service Worker update failed:", error);
            });
        }, 3600000); // 1시간마다 업데이트 체크

        // 페이지가 닫힐 때 인터벌 정리
        window.addEventListener("beforeunload", () => {
          clearInterval(intervalId);
        });
      })
      .catch((error) => {
        console.error("Service Worker registration failed:", error);
      });
  }
};

// 파이어베이스 메시징을 지원하고 모바일 os이며 PWA인 경우에만 실행
if (firebase.messaging.isSupported() && os && isPWA) {
  messaging = firebase.messaging();
}

if (isPWA) {
  // PWA로 실행 중일 때 theme컬러 변경
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", "#EF528F");
}

const userAgent = navigator.userAgent;
if (isPWA && !/KAKAOTALK/i.test(userAgent) && /Instagram/i.test(userAgent)) {
  navigator.permissions.query({ name: "notifications" }).then(async (permissionStatus) => {
    // PWA가 아니라면 얼리 리턴
    // ios와 android는 설정창에서 설정을 변경하고 나면 그 값이 서로 다르게 표시되기에 구분
    const iosPermission = Notification.permission === "granted";
    const androidPermission = permissionStatus.state === "granted";

    if (androidPermission || iosPermission) {
      console.log(`접속 기기 ${iosPermission ? "iphone" : "android"} 토큰을 등록해야 해요`);
      // 설정 창에서 상태가 변경되었을 때
      try {
        // 새로운 push 토큰을 받아옵니다.
        const pushToken = await messaging?.getToken({ vapidKey: env.FIREBASE.VAPID_KEY });
        // 이전에 저장된 push토큰을 가져옵니다.
        const prevPushToken = localStorage.getItem(LOCAL_STORAGE_KEY.pushToken);

        const token = localStorage.getItem(LOCAL_STORAGE_KEY.refreshTokenKey);

        // 푸시 토큰이 없다면 얼리 리턴함으로써 토큰을 저장하지 않음
        if (!pushToken) return;

        // 로그인중
        // 지원하는 os인 경우
        // PWA일 경우에만
        // 이전 푸시 토큰과 다른 경우 새로운 토큰 등록
        if (token && os && prevPushToken !== pushToken && isPWA) {
          try {
            await postFCMToken(pushToken, os);
            console.log(
              `접속 기기 ${iosPermission ? "iphone" : "android"} 토큰 등록을 성공하였어요`
            );
          } catch (err) {
            console.log("토큰 등록 요청 중 에러 발생");
          }
        } else {
          // 로그인 상태가 아니고 푸시 토큰이 있다면 토큰 스토리지에 저장
          console.log("알림 권한 거부됨");
          localStorage.setItem(LOCAL_STORAGE_KEY.pushToken, pushToken);
        }
      } catch (err) {
        console.error("푸시 토큰 요청 중 에러 발생:", err);
      }
    } else if (permissionStatus.state === "denied") {
      console.log("알림 허용 안 해요");
    }
  });
}

export default async function requestPermission(closeHandler: () => void) {
  // Firebase Messaging이 현재 브라우저에서 지원되며 모바일이며 PWA가 아니라면 얼리 리턴
  if (!firebase.messaging.isSupported() || !os || !isPWA) return;

  await navigator.serviceWorker.ready;

  try {
    const permission = await Notification.requestPermission();

    closeHandler();

    console.log(permission);

    if (permission === "granted") {
      const pushToken = await messaging?.getToken({ vapidKey: env.FIREBASE.VAPID_KEY });
      if (pushToken) {
        try {
          await postFCMToken(pushToken, os);
          localStorage.setItem(LOCAL_STORAGE_KEY.pushToken, pushToken);
          console.log("새로운 토큰 등록 성공");
        } catch (err) {
          console.log("토큰 등록 요청 중 에러 발생");
        }
      }
    } else if (permission === "denied") {
      console.log("푸시 권한 차단");
    }
  } catch (err) {
    console.log("푸시 토큰 가져오는 중에 에러 발생");
  } finally {
    // 로컬 스토리지에 알림 허용창을 보여줬다는 상태 저장
    localStorage.setItem("isFirstAlarmConfirm", JSON.stringify(false));
  }
}
